import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { PlatFormXDateTimeFormat } from "../../utils/helperFns";
import { ScheduleValue } from "../../utils/scheduleValidation";
import { CommonBoxWithNumber } from "../CommonBoxWithNumber/CommonBoxWithNumber";
import DatePicker from "../DatePicker/DatePicker";
import TitleSubTitle from "../SchemaComponents/TitleSubtitle/TitleSubTitle";

/**
 * Minutes ahead a freshly ticked checkbox pre-seeds. The seed is committed into state once, so it must
 * sit well clear of the 5-minute `MIN_SCHEDULE_LEAD_MINUTES` floor or an editor who accepts it and keeps
 * filling in a long form is refused at Publish for a time they never chose.
 */
const SEED_MINUTES_AHEAD = 15;

export type SchedulePublishUnpublishProps = {
  value: ScheduleValue;
  onChange: (next: ScheduleValue) => void;
  /** `section` wraps the control in a numbered form section; `panel` renders it bare. */
  variant?: "panel" | "section";
  /** Section number shown when `variant` is `section`, e.g. "02". */
  sectionNumber?: string;
  title?: string;
  subTitle?: string;
  disabled?: boolean;
  errors?: { publish?: string; unpublish?: string };
  /**
   * Schedules that already exist on the server (ISO-8601), e.g. from `scheduleFromTriggers`. A side with a
   * pending future time is LOCKED: shown ticked with that time, checkbox and picker disabled, and a note
   * pointing the editor at Reschedule / Cancel in the content list menu, which is the only place a
   * schedule is changed (KIWI-6762). The other side stays editable. The owner should still seed `value`
   * from the same times and pass them as `existing` to `planContentPublish`, so Publish never sends an
   * existing schedule again (the backend rejects a second schedule for a side that already has one).
   */
  pending?: { publish?: string | null; unpublish?: string | null };
};

type Side = "Publish" | "Unpublish";

/** A dayjs value from the picker, as an ISO-8601 UTC string; null if it is not a valid date. */
const toIsoOrNull = (picked: any): string | null => {
  if (!picked) return null;
  const valid = typeof picked.isValid === "function" ? picked.isValid() : true;
  return valid ? picked.toISOString() : null;
};

const seedTime = () => new Date(Date.now() + SEED_MINUTES_AHEAD * 60_000).toISOString();

/**
 * Schedule-publish / schedule-unpublish control for content types (KIWI-6576). Two checkboxes, each
 * enabling a date-and-time field. Fully controlled: it owns no state and knows nothing about which
 * content type it sits in, so the same control mounts in the Article settings dialog and inline in
 * schema-based forms.
 */
const SchedulePublishUnpublish = ({
  value,
  onChange,
  variant = "panel",
  sectionNumber = "",
  title,
  subTitle,
  disabled = false,
  errors,
  pending,
}: SchedulePublishUnpublishProps) => {
  const { t } = useTranslation();
  const idBase = useId();

  const heading = title ?? t("schedule_publish_unpublish_title");
  const description = subTitle ?? t("schedule_delay_note");

  // A time already in the past is a schedule that has run, not one that is pending.
  const pendingFor = (side: Side): string | null => {
    const at = side === "Publish" ? pending?.publish : pending?.unpublish;
    return at && new Date(at).getTime() > Date.now() ? at : null;
  };

  const setTicked = (side: Side, ticked: boolean) => {
    // A locked side cannot change here; its checkbox is disabled, this is belt and braces.
    if (pendingFor(side)) return;
    const seed = seedTime();
    if (side === "Publish") {
      onChange({
        ...value,
        isSchedulePublish: ticked,
        // Unticking clears the time so a stale date can never be sent.
        schedulePublishDateTime: ticked ? (value.schedulePublishDateTime ?? seed) : null,
      });
    } else {
      onChange({
        ...value,
        isScheduleUnpublish: ticked,
        scheduleUnpublishDateTime: ticked ? (value.scheduleUnpublishDateTime ?? seed) : null,
      });
    }
  };

  const setTime = (side: Side, picked: any) => {
    const iso = toIsoOrNull(picked);
    onChange(
      side === "Publish"
        ? { ...value, schedulePublishDateTime: iso }
        : { ...value, scheduleUnpublishDateTime: iso },
    );
  };

  const renderSide = (side: Side) => {
    const pendingAt = pendingFor(side);
    // A locked side always shows the server's schedule, whatever the owner's `value` holds.
    const locked = Boolean(pendingAt);
    const ticked =
      locked || (side === "Publish" ? value.isSchedulePublish : value.isScheduleUnpublish);
    const time = locked
      ? pendingAt
      : side === "Publish"
        ? value.schedulePublishDateTime
        : value.scheduleUnpublishDateTime;
    const error = locked ? undefined : side === "Publish" ? errors?.publish : errors?.unpublish;
    const label = t(side === "Publish" ? "page_schedule_publish" : "page_schedule_unpublish");
    const errorId = `${idBase}-${side}-error`;
    const pendingId = `${idBase}-${side}-pending`;
    const describedBy = [locked ? pendingId : "", error ? errorId : ""].filter(Boolean).join(" ");

    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}>
        <FormControlLabel
          sx={{ marginLeft: 0 }}
          control={
            <Checkbox
              sx={{ padding: "5px", marginRight: "6px" }}
              checked={ticked}
              disabled={disabled || locked}
              inputProps={locked ? { "aria-describedby": pendingId } : undefined}
              onChange={(event) => setTicked(side, event.target.checked)}
            />
          }
          label={
            <TitleSubTitle
              title={label}
              subTitle=''
              titleVariant='h6medium'
              subTitleVariant='p4regular'
            />
          }
        />
        <DatePicker
          time={time}
          handleValChange={(picked) => setTime(side, picked)}
          isDisabled={disabled || locked || !ticked}
          error={Boolean(error)}
          ariaLabel={label}
          ariaDescribedBy={describedBy || undefined}
        />
        {locked ? (
          <Typography id={pendingId} variant='p4regular'>
            {t("schedule_locked_note", { time: PlatFormXDateTimeFormat(pendingAt) })}
          </Typography>
        ) : null}
        {error ? (
          // Text, not just a red border: colour alone must not carry the error (WCAG 2.2 AA).
          <Typography
            id={errorId}
            role='alert'
            variant='p4regular'
            sx={{ color: (theme: any) => theme.palette.customColor?.ERROR }}>
            {error}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const body = (
    // Explicit `gap` (30px per the Figma), not Grid `spacing`: that is negative-margin plus item padding,
    // which any padding reset cancels, and it would only be 16px anyway.
    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: "30px" }}>
      {renderSide("Publish")}
      {renderSide("Unpublish")}
    </Box>
  );

  if (variant === "section") {
    return (
      <Box id='schedulePublish'>
        <CommonBoxWithNumber
          number={sectionNumber}
          title={heading}
          titleVarient='h5bold'
          subTitleVarient='p4regular'
          subTitle={description}>
          {body}
        </CommonBoxWithNumber>
      </Box>
    );
  }

  return (
    <Box id='schedulePublish'>
      <Box sx={{ marginBottom: "20px" }}>
        <TitleSubTitle
          title={heading}
          subTitle={description}
          titleVariant='h5bold'
          subTitleVariant='p4regular'
        />
      </Box>
      {body}
    </Box>
  );
};

export default SchedulePublishUnpublish;
